"use client";

import React, { useEffect, useId, useState } from "react";
import type { HTMLProps } from "react";
import Script from "next/script";
import cn from "classnames";
import cloneDeep from "lodash/cloneDeep";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body } from "@red-digital/bricks";
import { TypeFormWithoutUnresolvableLinksResponse } from "@2u-landing-pages/types";
import EventClient from "@2u-landing-pages/utils/event-client";
import { useFormEvents } from "./useFormEvents";
import Base64Encode from "./Base64Encode";
import airtrainJson from './airtrainForm.json';

import styles from "./LeadFormIntegrated.module.scss";

declare global {
  interface Window {
    LeadFormPlugin: any;
  }
}

interface LeadFormIntegratedProps extends HTMLProps<HTMLDivElement> {
  packageId?: string;
  enableStyles?: boolean;
  loadedClassName?: string;
  footnote?: TypeFormWithoutUnresolvableLinksResponse["fields"]["footnote"];
  title?: string;
  subtitle?: string;
  thankYouURL?: string;
  isLoading?: boolean;
  showFormHeader?: boolean;
  applyDomain?: string;
  leadFormType?: "TY" | "LP";
}

async function getPackageJSON(packageUrl: string) {
  return fetch(packageUrl)
    .then(function (response) {
      return response.json();
    })
    .then(function (responseJson){
      return responseJson;
    });
};

async function getPackageData(packageId: string, applyDomain: string, leadFormType: string, cb: Function) {
  let finalPackageData: any = {};

  if (leadFormType === 'LP') {
    finalPackageData = await getPackageJSON(`https://prospect-form-packages.2u.com/all/${packageId}.json`);
  } else if (leadFormType === 'TY') {
    finalPackageData = cloneDeep(airtrainJson);
    finalPackageData.applyDomain = applyDomain;
  }

  cb(finalPackageData);
}

export const LeadFormIntegrated: React.FC<LeadFormIntegratedProps> = ({
  className,
  loadedClassName = "",
  packageId = "",
  enableStyles = true,
  leadFormType = "TY",
  footnote,
  isLoading,
  thankYouURL,
  showFormHeader = false,
  title,
  subtitle,
  applyDomain = "",
  ...rest
}) => {
  const [loaded, setLoaded] = useState(false);
  const [packageLoaded, setPackageLoaded] = useState(false);
  const [formPackage, setFormPackage] = useState(null as any);
  const salt = useId();
  const formId = `${salt}-twou-form-${packageId || applyDomain.replace(/https?:\/\//, "").replace(/[.]/g, "-")}`;
  const { formContainerRef, steps } = useFormEvents(formId, leadFormType);
  const isLastStep = steps.current && steps.total && steps.current === steps.total;

  const handleExternalLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        htmlId: event.currentTarget.href,
        location: "LoadForm",
        name: event.currentTarget.innerText,
        position: "",
        text: "",
      },
    });
  };

  useEffect(() => {
    getPackageData(packageId, applyDomain, leadFormType, (pkg: any) => {
      setFormPackage(pkg);
      setPackageLoaded(true);
    });
  }, [leadFormType, packageId, applyDomain]);

  useEffect(() => {
    if (loaded && packageLoaded) {
      if (window.LeadFormPlugin) {
        try {
          const pkg = cloneDeep(formPackage);

          if (thankYouURL && pkg.thankYouPageURLs) {
            // Remove 2U's conditional thankYouPageURLs if our own thankYouURL is provided
            delete pkg.thankYouPageURLs;
          }

          const options = {
            package: pkg,
            templateType: 'element',
            templateFrom: `${salt}-lead-form-template`,
            salt,
            thankYouURL: thankYouURL || pkg?.embedFormData?.thankYouURL || '',
          };

          const TaxiForm = new window.LeadFormPlugin(window.document, formId, options);
          TaxiForm.start();
        } catch (e) {
          console.error(e);
        }
      } else {
        console.error('LeadFormPlugin is not defined');
      }
    }
  }, [loaded, packageLoaded, formPackage, salt, formId, thankYouURL]);

  const finalTitle = title ?? formPackage?.embedFormData?.title;
  const finalSubtitle = subtitle ?? formPackage?.embedFormData?.introText;

  return (
    <div className={className} ref={formContainerRef}>
      <code id={`${salt}-lead-form-template`} style={{ display: 'none' }} data-encoded="base64">
        <Base64Encode>
          <div id={`${salt}-lead-form-dialog`} className="card">
            <div className="card__body">
              {showFormHeader ?? (
                <div className="form-cta">
                  <div className="header-wrapper">
                    <h2 id={`${salt}-title`} className="h3" dangerouslySetInnerHTML={{ __html: finalTitle ?? '' }} />
                  </div>
                  <p id={`${salt}-intro-text`} dangerouslySetInnerHTML={{ __html: finalSubtitle ?? '' }} />
                </div>
              )}
              {/* <!-- The Progress Bar --> */}
              <div id={`${salt}-lead-form-progress-bar-mount-point`} />
            </div>
            {/* <!-- The Error Container --> */}
            <div id={`${salt}-lead-form-error-mount-point`} />
            <div className="form-body steps">
              <div data-name="leadForm">
                <div className="lead-form-steps auto-wrapper card__body">
                  {/* <!-- The Form Body --> */}
                  <div id={`${salt}-lead-form-field-mount-point`} />
                </div>
                <div className="card__footer">
                  {/* <!-- The Button --> */}
                  <button
                    className="lead-form-next-step button button--theme-cta button--block"
                    id={`${salt}-lead-form-next-action-button`}
                  >
                    Next Step
                  </button>
                  {/* <!-- The Footer --> */}
                  <div id={`${salt}-lead-form-footer-mount-point`} />
                </div>
              </div>
            </div>
          </div>
        </Base64Encode>
      </code>
      {!isLoading && (
        <>
          <div
            {...rest}
            id={formId}
            className={cn("self-mount-plugin", {
              [`${loadedClassName}`]: loaded,
              [styles.leadForm]: enableStyles,
              ["taxi-last-step"]: isLastStep,
              [styles.thankYouForm]: leadFormType === 'TY'
            })}
          />
          {footnote &&
            documentToReactComponents(footnote, {
              renderNode: {
                [BLOCKS.PARAGRAPH]: (_node: any, children: React.ReactNode) => (
                  <Body
                    className={cn(styles.footnote, {
                      [styles.footnoteHidden]: isLastStep,
                    })}
                    variant="caption-md"
                  >
                    {children}
                  </Body>
                ),
                [INLINES.HYPERLINK]: (node: any) => {
                  const { uri } = node?.data;
                  const content = node?.content?.[0];
                  if (content && "value" in content) {
                    return (
                      <a href={uri} target="_blank" rel="noopener noreferrer" onClick={handleExternalLinkClick}>
                        {content.value}
                      </a>
                    );
                  }
                  return null;
                },
              },
            })
          }
          <Script
            src="https://prospect-form-plugin.2u.com/latest/lead.form.plugin.js"
            onLoad={() => {
              setLoaded(true);
            }}
          />
        </>
      )}
    </div>
  );
};
